import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PAPER = '#F2EBDC';
const INK = '#1B1814';
const GOLD = '#A67C3A';

// Cost-analysis constants — based on actual provider pricing
const COSTS = {
  anthropic_per_day: 0.85,
  elevenlabs_per_reading: 0.45,
  whisper_per_checkin: 0.04,
  infra_per_user_month: 0.45,
};
const PRICE_PER_WEEK = 5.99;
const WEEKS_PER_MONTH = 4.33;
const REVENUE_PER_USER_MONTH = PRICE_PER_WEEK * WEEKS_PER_MONTH;

function calculateUserCost(user, completedDaysData, isVoiceUser) {
  const daysCompleted = (completedDaysData || []).filter(
    (d) => d.clerk_id === user.clerk_id
  ).length;

  const anthropicCost = daysCompleted * COSTS.anthropic_per_day;
  const elevenLabsCost = isVoiceUser
    ? daysCompleted * COSTS.elevenlabs_per_reading
    : daysCompleted * 0.08;
  const whisperCost = daysCompleted * COSTS.whisper_per_checkin;
  const infraCost = COSTS.infra_per_user_month;

  const totalCost = anthropicCost + elevenLabsCost + whisperCost + infraCost;
  const revenue =
    user.subscription_status === 'active' ? REVENUE_PER_USER_MONTH : 0;
  const profit = revenue - totalCost;

  return {
    anthropicCost,
    elevenLabsCost,
    whisperCost,
    infraCost,
    totalCost,
    revenue,
    profit,
    margin: revenue > 0 ? Math.round((profit / revenue) * 100) : null,
  };
}

export default function Admin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isAuthed, setIsAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  async function handleLogin() {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/admin-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const result = await response.json();
      if (response.ok) {
        setData(result);
        setIsAuthed(true);
      } else {
        setError(result.error || 'Incorrect password');
      }
    } catch (err) {
      console.error('[Admin] Login error:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  async function refresh() {
    setLoading(true);
    try {
      const response = await fetch('/api/admin-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const result = await response.json();
      if (response.ok) setData(result);
    } catch (err) {
      console.error('[Admin] Refresh error:', err);
    } finally {
      setLoading(false);
    }
  }

  function fmt(n, prefix = '$') {
    if (n >= 1000)
      return (
        prefix +
        n.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      );
    return prefix + n.toFixed(2);
  }

  function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (!isAuthed) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: INK,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            background: PAPER,
            borderRadius: 16,
            padding: '32px 28px',
            width: '100%',
            maxWidth: 360,
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: 11,
              color: GOLD,
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            Golden Fox Publishing
          </p>
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 24,
              fontWeight: 300,
              color: INK,
              marginBottom: 24,
            }}
          >
            Admin Dashboard
          </h1>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 10,
              border: '1.5px solid rgba(166,124,58,0.3)',
              background: PAPER,
              fontSize: 15,
              color: INK,
              marginBottom: 12,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {error && (
            <p
              style={{
                fontSize: 13,
                color: '#c93535',
                marginBottom: 12,
              }}
            >
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={handleLogin}
            disabled={loading}
            style={{
              width: '100%',
              background: INK,
              color: PAPER,
              border: 'none',
              borderRadius: 10,
              padding: '13px',
              fontSize: 15,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {loading ? 'Checking…' : 'Enter'}
          </button>
        </div>
      </div>
    );
  }

  const { summary, enneagramBreakdown, userList } = data;
  const completedDaysData = data.completedDays || [];

  // Per-user + aggregate cost figures (assumes voice usage by default).
  const userCosts = userList.map((u) =>
    calculateUserCost(u, completedDaysData, true)
  );
  const subscribedCosts = userCosts.filter((c) => c.revenue > 0);
  const totalCosts = {
    totalCost: userCosts.reduce((sum, c) => sum + c.totalCost, 0),
    revenue: userCosts.reduce((sum, c) => sum + c.revenue, 0),
    profit: userCosts.reduce((sum, c) => sum + c.profit, 0),
    elevenLabsTotal: userCosts.reduce(
      (sum, c) => sum + c.elevenLabsCost,
      0
    ),
    anthropicTotal: userCosts.reduce(
      (sum, c) => sum + c.anthropicCost,
      0
    ),
    whisperTotal: userCosts.reduce((sum, c) => sum + c.whisperCost, 0),
    avgCostPerUser:
      userList.length > 0
        ? userCosts.reduce((sum, c) => sum + c.totalCost, 0) /
          userList.length
        : 0,
    avgMargin:
      subscribedCosts.length > 0
        ? Math.round(
            subscribedCosts.reduce(
              (sum, c) => sum + (c.margin || 0),
              0
            ) / subscribedCosts.length
          )
        : 0,
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8f6f1',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <div
        style={{
          background: INK,
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <p
            style={{
              fontSize: 11,
              color: GOLD,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            Golden Fox Publishing
          </p>
          <h1
            style={{
              fontSize: 18,
              color: PAPER,
              fontWeight: 500,
              margin: '2px 0 0',
              fontFamily: 'Georgia, serif',
            }}
          >
            Rooted Admin
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            style={{
              background: 'rgba(255,255,255,0.1)',
              color: PAPER,
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              color: PAPER,
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Back to App
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 12,
            marginBottom: 24,
          }}
        >
          {[
            { label: 'Total Users', value: summary.totalUsers, color: INK },
            {
              label: 'Active Subscribers',
              value: summary.activeSubscriptions,
              color: '#1a7a45',
            },
            {
              label: 'Trial Users',
              value: summary.trialUsers,
              color: '#8a6200',
            },
            {
              label: 'Cancelled',
              value: summary.cancelledSubscriptions,
              color: '#922020',
            },
            {
              label: 'Conversion Rate',
              value: summary.conversionRate + '%',
              color: '#1a7a45',
            },
            {
              label: 'Weekly Revenue',
              value: fmt(summary.weeklyRevenue),
              color: '#1a7a45',
            },
            {
              label: 'Monthly Revenue',
              value: fmt(summary.monthlyRevenue),
              color: '#1a7a45',
            },
            {
              label: 'Annual Run Rate',
              value: fmt(summary.annualRevenue),
              color: '#1a7a45',
            },
          ].map((m, i) => (
            <div
              key={i}
              style={{
                background: 'white',
                borderRadius: 10,
                padding: '14px 16px',
                border: '0.5px solid rgba(0,0,0,0.08)',
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: '#888',
                  margin: '0 0 4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {m.label}
              </p>
              <p
                style={{
                  fontSize: 22,
                  fontWeight: 500,
                  color: m.color,
                  margin: 0,
                }}
              >
                {m.value}
              </p>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 0,
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            marginBottom: 20,
          }}
        >
          {['overview', 'users', 'costs'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 18px',
                fontSize: 13,
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                borderBottom:
                  activeTab === tab
                    ? `2px solid ${GOLD}`
                    : '2px solid transparent',
                color: activeTab === tab ? INK : '#888',
                fontWeight: activeTab === tab ? 500 : 400,
                textTransform: 'capitalize',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: 12,
                padding: '20px',
                border: '0.5px solid rgba(0,0,0,0.08)',
              }}
            >
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: INK,
                  marginBottom: 16,
                }}
              >
                Enneagram Distribution
              </h3>
              {Object.entries(enneagramBreakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <div
                    key={type}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        color: INK,
                        minWidth: 60,
                      }}
                    >
                      {type}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: 8,
                        background: '#f0ede6',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${(count / Math.max(summary.totalUsers, 1)) * 100}%`,
                          background: GOLD,
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 13,
                        color: '#888',
                        minWidth: 20,
                        textAlign: 'right',
                      }}
                    >
                      {count}
                    </span>
                  </div>
                ))}
              {Object.keys(enneagramBreakdown).length === 0 && (
                <p
                  style={{
                    fontSize: 13,
                    color: '#888',
                    fontStyle: 'italic',
                  }}
                >
                  No data yet
                </p>
              )}
            </div>

            <div
              style={{
                background: 'white',
                borderRadius: 12,
                padding: '20px',
                border: '0.5px solid rgba(0,0,0,0.08)',
              }}
            >
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: INK,
                  marginBottom: 16,
                }}
              >
                Engagement
              </h3>
              {[
                {
                  label: 'Avg days completed per user',
                  value: summary.avgDaysCompleted,
                },
                {
                  label: 'Active subscribers',
                  value: summary.activeSubscriptions,
                },
                { label: 'Trial users', value: summary.trialUsers },
                {
                  label: 'Conversion rate',
                  value: summary.conversionRate + '%',
                },
              ].map((m, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom:
                      i < 3 ? '0.5px solid #f0ede6' : 'none',
                  }}
                >
                  <span style={{ fontSize: 13, color: '#666' }}>
                    {m.label}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: INK,
                    }}
                  >
                    {m.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              border: '0.5px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: '#f8f6f1' }}>
                    {[
                      'Name',
                      'Email',
                      'Type',
                      'Status',
                      'Days',
                      'Weeks',
                      'Journal',
                      'Joined',
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '10px 14px',
                          textAlign: 'left',
                          color: '#888',
                          fontWeight: 400,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '1px solid rgba(0,0,0,0.08)',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {userList.map((u, i) => (
                    <tr
                      key={u.clerk_id}
                      style={{
                        borderBottom: '0.5px solid #f0ede6',
                        background: i % 2 === 0 ? 'white' : '#fdfcfa',
                      }}
                    >
                      <td
                        style={{
                          padding: '10px 14px',
                          fontWeight: 500,
                          color: INK,
                        }}
                      >
                        {u.name}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#666' }}>
                        {u.email}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span
                          style={{
                            background: 'rgba(166,124,58,0.1)',
                            color: GOLD,
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          {u.enneagram}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span
                          style={{
                            background:
                              u.subscription_status === 'active'
                                ? '#e6f7ed'
                                : u.subscription_status === 'cancelled'
                                  ? '#fdeaea'
                                  : '#fff8e6',
                            color:
                              u.subscription_status === 'active'
                                ? '#1a7a45'
                                : u.subscription_status === 'cancelled'
                                  ? '#922020'
                                  : '#8a6200',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          {u.subscription_status}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '10px 14px',
                          color: '#666',
                          textAlign: 'center',
                        }}
                      >
                        {u.days_completed}
                      </td>
                      <td
                        style={{
                          padding: '10px 14px',
                          color: '#666',
                          textAlign: 'center',
                        }}
                      >
                        {u.weeks_generated}
                      </td>
                      <td
                        style={{
                          padding: '10px 14px',
                          color: '#666',
                          textAlign: 'center',
                        }}
                      >
                        {u.journal_entries}
                      </td>
                      <td
                        style={{
                          padding: '10px 14px',
                          color: '#888',
                          fontSize: 12,
                        }}
                      >
                        {formatDate(u.joined)}
                      </td>
                    </tr>
                  ))}
                  {userList.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          padding: '32px',
                          textAlign: 'center',
                          color: '#888',
                          fontStyle: 'italic',
                        }}
                      >
                        No users yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'costs' && (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 12,
                marginBottom: 24,
              }}
            >
              {[
                {
                  label: 'Total Est. Monthly Cost',
                  value: fmt(totalCosts.totalCost),
                  color: '#922020',
                },
                {
                  label: 'Total Est. Revenue',
                  value: fmt(totalCosts.revenue),
                  color: '#1a7a45',
                },
                {
                  label: 'Est. Net Profit/Loss',
                  value:
                    (totalCosts.profit >= 0 ? '+' : '') +
                    fmt(totalCosts.profit),
                  color: totalCosts.profit >= 0 ? '#1a7a45' : '#922020',
                },
                {
                  label: 'Avg Cost Per User',
                  value: fmt(totalCosts.avgCostPerUser),
                  color: INK,
                },
                {
                  label: 'Avg Margin Per User',
                  value: totalCosts.avgMargin + '%',
                  color: totalCosts.avgMargin > 0 ? '#1a7a45' : '#922020',
                },
                {
                  label: 'ElevenLabs Est.',
                  value: fmt(totalCosts.elevenLabsTotal),
                  color: '#8a6200',
                },
                {
                  label: 'Anthropic Est.',
                  value: fmt(totalCosts.anthropicTotal),
                  color: '#8a6200',
                },
                {
                  label: 'Whisper Est.',
                  value: fmt(totalCosts.whisperTotal),
                  color: '#8a6200',
                },
              ].map((m, i) => (
                <div
                  key={i}
                  style={{
                    background: 'white',
                    borderRadius: 10,
                    padding: '14px 16px',
                    border: '0.5px solid rgba(0,0,0,0.08)',
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      color: '#888',
                      margin: '0 0 4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {m.label}
                  </p>
                  <p
                    style={{
                      fontSize: 20,
                      fontWeight: 500,
                      color: m.color,
                      margin: 0,
                    }}
                  >
                    {m.value}
                  </p>
                </div>
              ))}
            </div>

            <div
              style={{
                background: 'white',
                borderRadius: 12,
                padding: 20,
                border: '0.5px solid rgba(0,0,0,0.08)',
                marginBottom: 24,
              }}
            >
              <h3
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: INK,
                  marginBottom: 12,
                }}
              >
                Actual Billing Dashboards
              </h3>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                {[
                  {
                    name: 'Anthropic',
                    url: 'https://console.anthropic.com/settings/billing',
                  },
                  {
                    name: 'ElevenLabs',
                    url: 'https://elevenlabs.io/app/subscription',
                  },
                  {
                    name: 'OpenAI (Whisper)',
                    url: 'https://platform.openai.com/usage',
                  },
                  {
                    name: 'Stripe',
                    url: 'https://dashboard.stripe.com/balance',
                  },
                  {
                    name: 'Vercel',
                    url: 'https://vercel.com/golden-fox/~/usage',
                  },
                ].map((provider) => (
                  <a
                    key={provider.name}
                    href={provider.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: '#f8f6f1',
                      border: '0.5px solid rgba(0,0,0,0.1)',
                      borderRadius: 8,
                      padding: '8px 14px',
                      fontSize: 13,
                      color: INK,
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {provider.name} →
                  </a>
                ))}
              </div>
              <p
                style={{
                  fontSize: 11,
                  color: '#888',
                  marginTop: 12,
                  fontStyle: 'italic',
                }}
              >
                Estimates above are calculated from usage data. Click
                above for actual charges.
              </p>
            </div>

            <div
              style={{
                background: 'white',
                borderRadius: 12,
                border: '0.5px solid rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '0.5px solid rgba(0,0,0,0.08)',
                }}
              >
                <h3
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: INK,
                    margin: 0,
                  }}
                >
                  Cost Per User
                </h3>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr style={{ background: '#f8f6f1' }}>
                      {[
                        'User',
                        'Status',
                        'Days',
                        'Anthropic',
                        'ElevenLabs',
                        'Whisper',
                        'Infra',
                        'Total Cost',
                        'Revenue',
                        'Profit',
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: '10px 14px',
                            textAlign: 'left',
                            color: '#888',
                            fontWeight: 400,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {userList.map((u, i) => {
                      const costs = calculateUserCost(
                        u,
                        completedDaysData,
                        true
                      );
                      return (
                        <tr
                          key={u.clerk_id}
                          style={{
                            borderBottom: '0.5px solid #f0ede6',
                            background: i % 2 === 0 ? 'white' : '#fdfcfa',
                          }}
                        >
                          <td
                            style={{
                              padding: '10px 14px',
                              fontWeight: 500,
                              color: INK,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {u.name}
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <span
                              style={{
                                background:
                                  u.subscription_status === 'active'
                                    ? '#e6f7ed'
                                    : '#fff8e6',
                                color:
                                  u.subscription_status === 'active'
                                    ? '#1a7a45'
                                    : '#8a6200',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                              }}
                            >
                              {u.subscription_status}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              color: '#666',
                              textAlign: 'center',
                            }}
                          >
                            {u.days_completed}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              color: '#666',
                            }}
                          >
                            ${costs.anthropicCost.toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              color: '#666',
                            }}
                          >
                            ${costs.elevenLabsCost.toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              color: '#666',
                            }}
                          >
                            ${costs.whisperCost.toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              color: '#666',
                            }}
                          >
                            ${costs.infraCost.toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              fontWeight: 500,
                              color: '#922020',
                            }}
                          >
                            ${costs.totalCost.toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              fontWeight: 500,
                              color: '#1a7a45',
                            }}
                          >
                            ${costs.revenue.toFixed(2)}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              fontWeight: 500,
                              color:
                                costs.profit >= 0 ? '#1a7a45' : '#922020',
                            }}
                          >
                            {costs.profit >= 0 ? '+' : ''}
                            {costs.profit.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                    {userList.length === 0 && (
                      <tr>
                        <td
                          colSpan={10}
                          style={{
                            padding: '32px',
                            textAlign: 'center',
                            color: '#888',
                            fontStyle: 'italic',
                          }}
                        >
                          No users yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
